/**
 * `ConfirmDialog` — ported from `Foyer_Dashboard_CURRENT.jsx` line 410.
 * Modal wrapping a confirmation prompt with optional consequences list and
 * tone-aware confirm button.
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';
import { Modal } from './Modal.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export type ConfirmTone = 'lime' | 'coral' | 'amber';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  confirmTone?: ConfirmTone;
  onConfirm: () => void;
  icon?: LucideIcon;
  consequences?: string[];
  /** Label for the dismiss button; say what NOT confirming means. Default "Cancel". */
  cancelLabel?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  confirmTone = 'lime',
  onConfirm,
  icon: Icon,
  consequences,
  cancelLabel = 'Cancel',
}: ConfirmDialogProps): JSX.Element | null {
  const C = useC();
  if (!open) return null;
  const tones: Record<ConfirmTone, { bg: string; fg: string }> = {
    lime: { bg: C.lime, fg: C.ink },
    coral: { bg: C.coral, fg: '#FFFFFF' },
    amber: { bg: C.amber, fg: C.ink },
  };
  const t = tones[confirmTone];
  return (
    <Modal open={open} onClose={onClose} size="sm">
      <div className="p-6">
        {Icon ? (
          <div
            className="flex items-center justify-center mb-4"
            style={{
              width: 44,
              height: 44,
              background: confirmTone === 'coral' ? C.coralDim : C.limeDim,
              borderRadius: 4,
            }}
          >
            <Icon size={20} color={confirmTone === 'coral' ? C.coralFg : C.limeFg} />
          </div>
        ) : null}
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 26,
            color: C.text,
            letterSpacing: -0.5,
            lineHeight: 1.15,
            marginBottom: 8,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.muted,
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          {body}
        </div>
        {consequences ? (
          <div
            className="p-3 mb-5"
            style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1,
                marginBottom: 6,
              }}
            >
              WHAT HAPPENS NEXT
            </div>
            <ul className="space-y-1">
              {consequences.map((c, i) => (
                <li
                  key={i}
                  style={{ fontFamily: sansFont, fontSize: 12, color: C.text, display: 'flex', gap: 8 }}
                >
                  <span style={{ color: C.dim }}>—</span> {c}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.muted,
              fontFamily: sansFont,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="w-full sm:flex-1 px-4 py-2.5"
            style={{
              background: t.bg,
              color: t.fg,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
